/**
 * Transport-service pricing helper — frontend mirror of the backend's
 * `calculateTransportSegmentPrice` (functions/src/utils/pricing.ts:242-267).
 *
 * Business rule (canonical, see docs/plans/2026-05-15-TRANSPORT-ACTIVITY.md):
 *   - `basePrice.amount` is a FLAT per-segment fare covering up to `baseOccupancy`
 *     passengers. The `basePrice.perPerson: true` flag in Firestore is vestigial
 *     for transport — the admin schema forces it to `true` for all activities,
 *     but the transport engine ignores it by design.
 *   - Every passenger past `baseOccupancy` adds `extraGuestPrice.amount` to that
 *     segment's total.
 *   - `dailyPricing[date]` overrides `basePrice.amount` per segment when set.
 *
 * Keep this helper aligned 1:1 with `calculateTransportSegmentPrice`. Divergence
 * causes Stripe's amount-mismatch guard (payments.ts:289-313) to reject the
 * PaymentIntent because cart total != booking.totalAmount persisted server-side.
 */

export interface TransportPricingInput {
  basePriceAmount: number;
  extraGuestAmount?: number;
  baseOccupancy?: number;
  dailyPricing?: Record<string, number>;
}

export interface TransportSegmentInput {
  date: string;
}

/**
 * Price a single transport segment using the flat-rate + extra-guest surcharge
 * model. Mirror of backend `calculateTransportSegmentPrice`.
 */
export function calculateTransportSegmentUSD(
  pricing: TransportPricingInput,
  segment: TransportSegmentInput,
  guests: number,
): number {
  const baseOccupancy = pricing.baseOccupancy ?? guests;
  const extraGuestRate = pricing.extraGuestAmount ?? 0;
  const dailyOverride = pricing.dailyPricing?.[segment.date];
  const flatPrice = dailyOverride !== undefined ? dailyOverride : pricing.basePriceAmount;
  const extraGuests = Math.max(0, guests - baseOccupancy);
  const extraFee = extraGuests * extraGuestRate;
  return flatPrice + extraFee;
}

/**
 * Sum across 1+ segments (outbound + optional return).
 */
export function calculateTransportTotalUSD(
  pricing: TransportPricingInput,
  segments: TransportSegmentInput[],
  guests: number,
): number {
  return segments.reduce(
    (sum, segment) => sum + calculateTransportSegmentUSD(pricing, segment, guests),
    0,
  );
}
